import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';
import {
  getUnitIdFromNodeId,
  isVcAgentBomSocketProcessingStatus,
  normalizeTabStatus,
} from 'utils/AICreatorHelper';

import type IAICreatorSupplierUnitsTabItem from '../interfaces/IAICreatorSupplierUnitsTabItem';
import type IUseAICreatorSupplierTabsParams from '../interfaces/IUseAICreatorSupplierTabsParams';

const useAICreatorSupplierTabs = ({
  open,
  selectedCards,
  session,
  sessionUnitStatusByUnitId,
  valueChainMapId,
  sessionId,
  updateAgentSessionSupplierDiscovery,
  pendingAiUnitIds,
  initialActiveTabNodeId,
  onBadRequestError,
}: IUseAICreatorSupplierTabsParams) => {
  const [activeTabNodeId, setActiveTabNodeId] = useState<string>('');
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [tabs, setTabs] = useState<IAICreatorSupplierUnitsTabItem[]>([]);
  const [
    supplierUnitStatusOverrideByUnitId,
    setSupplierUnitStatusOverrideByUnitId,
  ] = useState<Map<number, string>>(() => new Map());

  useEffect(() => {
    const pendingSet = new Set(pendingAiUnitIds);

    const nextTabs: IAICreatorSupplierUnitsTabItem[] = selectedCards.map((card, index) => {
      const cardUnitId = getUnitIdFromNodeId(card.nodeId);
      const localStatus = cardUnitId !== null
        ? supplierUnitStatusOverrideByUnitId.get(cardUnitId)
        : undefined;
      const discoveryStatus = cardUnitId !== null ? sessionUnitStatusByUnitId.get(cardUnitId) : undefined;
      const selectedUnit = session?.selectedUnits.find((sessionUnit, sessionUnitIndex) => {
        if (cardUnitId !== null) {
          return sessionUnit.unitId === cardUnitId;
        }

        return sessionUnitIndex === index;
      });

      const rawStatus = localStatus ?? discoveryStatus ?? selectedUnit?.status;
      const normalizedStatus = normalizeTabStatus(rawStatus);
      const isLocallyConfirmed = localStatus === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;
      const serverStatus = normalizedStatus === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT
        && !isLocallyConfirmed
        ? VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
        : normalizedStatus;
      const isProcessingByAgentSession = isVcAgentBomSocketProcessingStatus(selectedUnit?.status);
      const isPendingAiGeneration = cardUnitId !== null && pendingSet.has(cardUnitId);
      const status = isPendingAiGeneration || isProcessingByAgentSession
        ? VALUE_CHAIN_AI_AGENT.AI_STATUS
        : serverStatus;

      return {
        ...card,
        status,
      };
    });

    setTabs(nextTabs);
  }, [
    pendingAiUnitIds,
    selectedCards,
    session?.selectedUnits,
    sessionUnitStatusByUnitId,
    supplierUnitStatusOverrideByUnitId,
  ]);

  useEffect(() => {
    if (!open) {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }

      return undefined;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setActiveTabNodeId('');
      setSupplierUnitStatusOverrideByUnitId((prev) => (prev.size === 0 ? prev : new Map()));
      return;
    }

    const hasActiveTab = tabs.some((tab) => tab.nodeId === activeTabNodeId);

    if (!hasActiveTab) {
      setActiveTabNodeId(tabs[0]?.nodeId || '');
    }
  }, [activeTabNodeId, open, tabs]);

  useEffect(() => {
    if (!open || !initialActiveTabNodeId) {
      return;
    }

    const hasRequestedTab = tabs.some((tab) => tab.nodeId === initialActiveTabNodeId);

    if (!hasRequestedTab || activeTabNodeId === initialActiveTabNodeId) {
      return;
    }

    setActiveTabNodeId(initialActiveTabNodeId);
  }, [activeTabNodeId, initialActiveTabNodeId, open, tabs]);

  const activeTab = useMemo(() => {
    const matched = tabs.find((tab) => tab.nodeId === activeTabNodeId);

    if (matched) {
      return matched;
    }

    if (!activeTabNodeId && tabs[0]) {
      return tabs[0];
    }

    return undefined;
  }, [activeTabNodeId, tabs]);

  const activeTabIndex = useMemo(
    () => tabs.findIndex((tab) => tab.nodeId === activeTab?.nodeId),
    [activeTab?.nodeId, tabs],
  );

  const setSupplierUnitStatusOverride = useCallback((unitId: number, status: string) => {
    setSupplierUnitStatusOverrideByUnitId((prev) => {
      if (prev.get(unitId) === status) {
        return prev;
      }

      const next = new Map(prev);

      next.set(unitId, status);

      return next;
    });
  }, []);

  const syncCurrentSupplierStatusInProgress = useCallback(async (unitId: number, hasItems: boolean) => {
    if (!valueChainMapId || !sessionId || !unitId || !activeTab) {
      return;
    }

    const nextStatus = hasItems
      ? VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
      : VALUE_CHAIN_AI_AGENT.STATUS_EMPTY;
    const nextStatusLegacy = hasItems
      ? VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS_LEGACY
      : VALUE_CHAIN_AI_AGENT.STATUS_EMPTY;

    if (activeTab.status === nextStatus || activeTab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS) {
      return;
    }

    setTabs((prevTabs) => prevTabs.map((tab) => (
      tab.nodeId === activeTab.nodeId
        ? { ...tab, status: nextStatus }
        : tab
    )));
    setSupplierUnitStatusOverride(unitId, nextStatus);

    await updateAgentSessionSupplierDiscovery({
      valueChainMapId,
      sessionId,
      unitId,
      status: nextStatusLegacy,
    }).unwrap().catch((error) => {
      onBadRequestError(error);
    });
  }, [
    activeTab,
    onBadRequestError,
    sessionId,
    setSupplierUnitStatusOverride,
    updateAgentSessionSupplierDiscovery,
    valueChainMapId,
  ]);

  return {
    tabs,
    setTabs,
    activeTabNodeId,
    setActiveTabNodeId,
    isSelectAllChecked,
    setIsSelectAllChecked,
    activeTab,
    activeTabIndex,
    setSupplierUnitStatusOverride,
    syncCurrentSupplierStatusInProgress,
  };
};

export default useAICreatorSupplierTabs;
